<p *ngIf="loading" class="muted">Loading dashboard…</p>
<p *ngIf="!loading && errorMsg" class="error">{{ errorMsg }}</p>

<div class="dash" *ngIf="!loading && !errorMsg">
  <!-- Header -->
  <header class="dash__header">
    <div class="dash__title">
      <h2>Dashboard</h2>
      <p class="dash__subtitle">
        Overview of orders, revenue, and operational status
        <span class="dot">•</span>
        <span class="tz">Timezone: {{ timezone }}</span>
      </p>
    </div>

    <div class="dash__headerActions">
      <button type="button" class="btn btn--ghost" (click)="refresh()">
        Refresh
      </button>
    </div>
  </header>

  <!-- KPI Row -->
  <section class="kpis" aria-label="Key performance indicators">
    <article class="card kpi">
      <div class="kpi__top">
        <p class="kpi__label">Today</p>
        <p class="kpi__hint">Orders & revenue</p>
      </div>

      <div class="kpi__main">
        <div class="kpi__value">
          <span class="big">{{ totals.today.totalOrders }}</span>
          <span class="unit">orders</span>
        </div>
        <div class="kpi__value">
          <span class="big">£{{ totals.today.totalRevenue }}</span>
          <span class="unit">revenue</span>
        </div>
      </div>
    </article>

    <article class="card kpi">
      <div class="kpi__top">
        <p class="kpi__label">This week</p>
        <p class="kpi__hint">Orders & revenue</p>
      </div>

      <div class="kpi__main">
        <div class="kpi__value">
          <span class="big">{{ totals.week.totalOrders }}</span>
          <span class="unit">orders</span>
        </div>
        <div class="kpi__value">
          <span class="big">£{{ totals.week.totalRevenue }}</span>
          <span class="unit">revenue</span>
        </div>
      </div>
    </article>

    <article class="card kpi">
      <div class="kpi__top">
        <p class="kpi__label">This month</p>
        <p class="kpi__hint">Orders & revenue</p>
      </div>

      <div class="kpi__main">
        <div class="kpi__value">
          <span class="big">{{ totals.month.totalOrders }}</span>
          <span class="unit">orders</span>
        </div>
        <div class="kpi__value">
          <span class="big">£{{ totals.month.totalRevenue }}</span>
          <span class="unit">revenue</span>
        </div>
      </div>
    </article>
  </section>

  <!-- Main grid -->
  <section class="grid">
    <!-- Status analytics -->
    <article class="card panel panel--wide">
      <header class="panel__header">
        <div>
          <h3>Order status analytics</h3>
          <p class="panel__meta">
            {{ activeStatusBlock?.rangeLabel }}
          </p>
        </div>

        <div class="seg" role="tablist" aria-label="Select time range">
          <button
            type="button"
            class="seg__btn"
            [class.is-active]="activeRange === '24h'"
            (click)="setRange('24h')"
            role="tab"
            [attr.aria-selected]="activeRange === '24h'"
          >
            24h
          </button>
          <button
            type="button"
            class="seg__btn"
            [class.is-active]="activeRange === '7d'"
            (click)="setRange('7d')"
            role="tab"
            [attr.aria-selected]="activeRange === '7d'"
          >
            7d
          </button>
          <button
            type="button"
            class="seg__btn"
            [class.is-active]="activeRange === 'month'"
            (click)="setRange('month')"
            role="tab"
            [attr.aria-selected]="activeRange === 'month'"
          >
            Month
          </button>
          <button
            type="button"
            class="seg__btn"
            [class.is-active]="activeRange === 'year'"
            (click)="setRange('year')"
            role="tab"
            [attr.aria-selected]="activeRange === 'year'"
          >
            Year
          </button>
          <button
            type="button"
            class="seg__btn"
            [class.is-active]="activeRange === 'all'"
            (click)="setRange('all')"
            role="tab"
            [attr.aria-selected]="activeRange === 'all'"
          >
            All
          </button>
        </div>
      </header>

      <!-- Status list -->
      <div
        class="statusList"
        *ngIf="activeStatusBlock?.rows?.length; else noStatusData"
      >
        <div class="statusRow" *ngFor="let row of activeStatusBlock.rows">
          <div class="statusRow__left">
            <span class="pill" [attr.data-kind]="row.kind">{{
              row.label
            }}</span>
            <span class="count">{{ row.count }}</span>
          </div>

          <div class="statusRow__bar" aria-hidden="true">
            <span class="statusRow__fill" [style.width.%]="row.percent"></span>
          </div>

          <div class="statusRow__right">
            <span class="pct">{{ row.percent | number: "1.0-0" }}%</span>
          </div>
        </div>

        <footer class="panel__footer">
          <div class="miniMeta">
            <span class="miniMeta__k">Total statuses:</span>
            <span class="miniMeta__v">{{ activeStatusBlock.total }}</span>
          </div>
          <div
            class="miniMeta"
            *ngIf="activeStatusBlock.rangeStart !== 'NotSet'"
          >
            <span class="miniMeta__k">Range:</span>
            <span class="miniMeta__v">
              {{ activeStatusBlock.rangeStart }} →
              {{ activeStatusBlock.rangeEnd }}
            </span>
          </div>
        </footer>
      </div>

      <ng-template #noStatusData>
        <p class="muted">No status data available for this range.</p>
      </ng-template>
    </article>

    <!-- Attention needed -->
    <article class="card panel">
      <header class="panel__header">
        <div>
          <h3>Attention needed</h3>
          <p class="panel__meta">Quick operational signals</p>
        </div>
      </header>

      <div class="attention">
        <div class="attention__item" [class.is-warn]="attention.pending > 0">
          <div class="attention__top">
            <p class="attention__label">Pending</p>
            <p class="attention__value">{{ attention.pending }}</p>
          </div>
          <p class="attention__desc">
            Orders waiting for action (accept/reject).
          </p>
          <a class="link" routerLink="/live-orders">Go to live orders</a>
        </div>

        <div class="attention__item" [class.is-ok]="attention.inProgress > 0">
          <div class="attention__top">
            <p class="attention__label">In progress</p>
            <p class="attention__value">{{ attention.inProgress }}</p>
          </div>
          <p class="attention__desc">
            Confirmed / cooking / delivery right now.
          </p>
          <a class="link" routerLink="/live-orders">Monitor</a>
        </div>

        <div
          class="attention__item"
          [class.is-muted]="attention.cancelled24h === 0"
        >
          <div class="attention__top">
            <p class="attention__label">Cancelled (24h)</p>
            <p class="attention__value">{{ attention.cancelled24h }}</p>
          </div>
          <p class="attention__desc">
            If this spikes, check stock, hours, and payment issues.
          </p>
        </div>
      </div>
    </article>
  </section>
</div>
